"use client"

import ArticleBox from '../ArticleBox/ArticleBox';
import { useEffect, useState, useCallback, useRef } from "react";
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { storyblokInit, apiPlugin, getStoryblokApi } from "@storyblok/react";

// ✅ Dynamically import Lottie with SSR disabled
const Lottie = dynamic(() => import("lottie-react"), { ssr: false });
import blogLottie from "../../assets/bloglottie.json";
import Link from 'next/link';

storyblokInit({
    accessToken: process.env.NEXT_PUBLIC_STORYBLOK_TOKEN,
    use: [apiPlugin], // Enable API Plugin
});

function formatDateToLong(dateTimeStr) {
    const date = new Date(dateTimeStr.replace(" ", "T")); // Ensure valid ISO format
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
}

// Loading skeleton component
const CardSkeleton = () => (
    <div className="relative flex-1 p-[4px] rounded-lg bg-gradient-to-b from-[#D259A6]/80 to-[#48398C]/50 w-full animate-pulse">
        <div className="h-full w-full bg-ASMBenefitsBg rounded-lg flex flex-col justify-start items-center text-center py-[40px]">
            <div className="h-8 w-3/4 bg-gray-200 rounded mb-2"></div>
            <div className="h-[95px] w-[95px] bg-gray-200 rounded-full mb-6"></div>
            <div className="h-6 w-1/2 bg-gray-200 rounded mb-4"></div>
            <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
            <div className="flex justify-between items-center mt-[69px] gap-[25px]">
                <div className="w-[38px] h-[46.69px] bg-gray-200 rounded"></div>
                <div className="h-6 w-24 bg-gray-200 rounded"></div>
            </div>
        </div>
    </div>
);

function DiscoverZeroDays({ blok }) {
    const [allZeroDays, setAllZeroDays] = useState([]);
    const [tags, setTags] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const perPage = 6;
    const isFetching = useRef(false);
    const currentPage = useRef(1);
    const lastFetchedPage = useRef(0);
    const scrollTimeout = useRef(null);
    const loadingTimeout = useRef(null);

    const fetchZeroDaysPage = useCallback(async (page) => {
        if (isFetching.current || page === lastFetchedPage.current) return;
        
        isFetching.current = true;
        lastFetchedPage.current = page;
        setLoading(true);
        setShowLoading(true);
        setError(null);

        try {
            // Show loading animation for at least 1 second
            const startTime = Date.now();
            
            const storyblokApi = getStoryblokApi();
            const { data } = await storyblokApi.get("cdn/stories", {
                starts_with: "zero-day/",
                per_page: perPage,
                page: page,
            });

            // Convert date strings to proper Date objects for sorting
            const sortedStories = data.stories.map(story => {
                const zeroDaysHero = story.content.body.find(item => item.component === "Zero Days Hero");
                if (zeroDaysHero?.published_date) {
                    zeroDaysHero.parsedDate = new Date(Date.parse(zeroDaysHero.published_date));
                }
                return { ...story, zeroDaysHero };
            });

            // Sort stories by date (newest first)
            sortedStories.sort((a, b) => (b.zeroDaysHero?.parsedDate || 0) - (a.zeroDaysHero?.parsedDate || 0));

            // Calculate remaining time to show loading
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(1000 - elapsedTime, 0);

            // Wait for the remaining time
            await new Promise(resolve => setTimeout(resolve, remainingTime));

            setAllZeroDays(prev => {
                if (page === 1) return sortedStories;
                const newStories = sortedStories.filter(
                    newStory => !prev.some(existingStory => existingStory.id === newStory.id)
                );
                return [...prev, ...newStories];
            });

            setHasMore(data.stories.length === perPage);
            currentPage.current = page;
        } catch (err) {
            console.error("Error fetching Zero days:", err);
            setError("Failed to load more stories. Please try again.");
            lastFetchedPage.current = page - 1;
        } finally {
            setLoading(false);
            // Keep showing loading animation for a bit longer
            setTimeout(() => {
                setShowLoading(false);
                isFetching.current = false;
            }, 500);
        }
    }, [perPage]);

    useEffect(() => {
        fetchZeroDaysPage(1);
    }, [fetchZeroDaysPage]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const lastEntry = entries[0];
                if (lastEntry.isIntersecting && !loading && hasMore && !isFetching.current) {
                    const nextPage = currentPage.current + 1;
                    if (nextPage !== lastFetchedPage.current) {
                        fetchZeroDaysPage(nextPage);
                    }
                }
            },
            {
                root: null,
                rootMargin: '200px', // Increased margin for better desktop detection
                threshold: [0, 0.1, 0.2] // Multiple thresholds for better detection
            }
        );

        // Function to observe the last card
        const observeLastCard = () => {
            const cards = document.querySelectorAll('.grid.lg\\:grid-cols-3 > *');
            if (cards.length > 0) {
                // Observe both the last card and the second-to-last card for better desktop detection
                const lastCard = cards[cards.length - 1];
                const secondLastCard = cards[cards.length - 2];
                
                observer.observe(lastCard);
                if (secondLastCard) {
                    observer.observe(secondLastCard);
                }
            }
        };

        // Initial observation
        observeLastCard();

        // Observe on resize and when new cards are added
        const resizeObserver = new ResizeObserver(() => {
            observer.disconnect();
            observeLastCard();
        });

        const gridContainer = document.querySelector('.grid.lg\\:grid-cols-3');
        if (gridContainer) {
            resizeObserver.observe(gridContainer);
        }

        // Also observe on scroll to handle desktop layout better
        const handleScroll = () => {
            observer.disconnect();
            observeLastCard();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            observer.disconnect();
            resizeObserver.disconnect();
            window.removeEventListener('scroll', handleScroll);
        };
    }, [loading, hasMore, fetchZeroDaysPage]);

    return (
        <div className='m-auto w-full'>
            <div className='bg-asmPageTopBG' style={{
                backgroundSize: "cover",
                backgroundPosition: "bottom",
                backgroundRepeat: "repeat",
                margin: 'auto'
            }}>
                <div className='m-auto'>
                    <div className="relative lg:min-h-[1000px] lg:flex flex-col items-center py-8 m-auto">
                        <div className="font-aileron pt-20 lg:pt-32 text-center font-bold max-w-7xl">
                            <div className="bg-custom-radial bg-clip-text text-transparent text-[48px] lg:text-[80px] p-4 lg:p-0">
                                {blok.heading}
                            </div>
                        </div>
                        <div className='lg:w-7xl lg:p-0 p-4'>
                            <div className='flex flex-col items-center text-center justify-center w-[100%] m-auto py-14'>
                                {error && (
                                    <div className="text-red-500 mb-4">
                                        {error}
                                    </div>
                                )}
                                <div className='grid lg:grid-cols-3 gap-10 w-full'>
                                    {allZeroDays?.map((card, index) => {
                                        const cardContent = card?.content?.body;
                                        const { lead, subheading,published_date,publisher_icon } = cardContent?.find(item => item.component === "Zero Days Hero") || {};
                                        return (
                                            <Link
                                                href={`/zero-day/${card.slug}`}
                                                key={`zc-${card.id || index}`}
                                                className="relative flex-1 p-[4px] rounded-lg bg-gradient-to-b from-[#D259A6]/80 to-[#48398C]/50 w-full"
                                            >
                                                <div className="h-full w-full bg-ASMBenefitsBg rounded-lg flex flex-col justify-start items-center text-center py-[40px]">
                                                    <p className="font-lato text-[24px] !text-[#49378F] font-bold mb-2" style={{ color: "#49378F" }}>
                                                        {lead}
                                                    </p>
                                                    <img src={publisher_icon?.filename	|| "/zdten.svg"} className="h-[95px] w-[95px] mb-6" alt="Zero Day Icon" />
                                                    <p className="text-[24px] font-bold font-lato w-[80%]" style={{ color: "#49378F" }}></p>
                                                    <p className="text-[18px] font-lato font-normal w-[80%]">{subheading}</p>
                                                    <div className='flex justify-between items-center mt-[69px] gap-[25px]'>
                                                        <img src={"/calendar.svg"} className='w-[38px] h-[46.69px] object-cover' alt="Calendar Icon" />
                                                        <p className='text-[24px] font-normal'>
                                                            {published_date ? formatDateToLong(published_date) : ""}
                                                        </p>
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                                {showLoading && (
                                    <div className="flex flex-col items-center justify-center w-full py-8">
                                        <Lottie animationData={blogLottie} className='w-32 h-32' />
                                        {/* <p className="text-gray-500 mt-4">Loading more stories...</p> */}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DiscoverZeroDays;
